import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Hero() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const videoY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const card1Y = useTransform(scrollYProgress, [0, 0.8], ['0%', '-25%']);
  const card2Y = useTransform(scrollYProgress, [0, 0.8], ['0%', '-18%']);
  const card3Y = useTransform(scrollYProgress, [0, 0.8], ['0%', '-30%']);

  const features = [
    t('phoenix.feature1'),
    t('phoenix.feature2'),
    t('phoenix.feature3'),
    t('phoenix.feature4'),
  ];

  const trustItems = [
    { icon: 'ri-shield-check-line', text: t('phoenix.trust1') },
    { icon: 'ri-flashlight-line', text: t('phoenix.trust2') },
    { icon: 'ri-user-heart-line', text: t('phoenix.trust3') },
  ];

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center bg-white overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-accent-500/[0.03] rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary-500/[0.02] rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

      <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10 flex flex-col items-center gap-10 lg:gap-14">
        {/* Video — lo primero y lo más grande */}
        <motion.div style={{ y: videoY, opacity }} className="w-full max-w-[980px] relative">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-500/15 via-transparent to-primary-500/10 rounded-3xl blur-2xl"></div>

            <div className="relative rounded-3xl overflow-hidden border border-background-200 shadow-[0_8px_40px_rgba(0,0,0,0.06),0_2px_12px_rgba(0,0,0,0.04)] bg-white flex items-center justify-center">
              <video
                src="https://storage.helloreaddy.io/project_files/3dceec99-0a90-4550-9724-ce762c1f57f1/e5678c45-4d22-422f-a417-0f69c4a64671_VSL-MENTORING.mp4"
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto object-contain"
              />
            </div>

            <motion.div
              style={{ y: card1Y }}
              className="absolute -left-6 top-8 bg-white/80 backdrop-blur-xl rounded-2xl border border-background-200/60 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-line-chart-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">ROAS</p>
                <p className="text-lg font-bold text-foreground-950 leading-tight">4.8<span className="text-xs text-foreground-500">x</span></p>
              </div>
            </motion.div>

            <motion.div
              style={{ y: card2Y }}
              className="absolute -right-4 top-20 bg-white/80 backdrop-blur-xl rounded-2xl border border-background-200/60 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className="ri-user-add-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">Leads</p>
                <p className="text-lg font-bold text-foreground-950 leading-tight">1,240<span className="text-xs text-green-500 ml-0.5">+24%</span></p>
              </div>
            </motion.div>

            <motion.div
              style={{ y: card3Y }}
              className="absolute -right-5 bottom-14 bg-white/80 backdrop-blur-xl rounded-2xl border border-background-200/60 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-accent-600 text-lg"></i>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">Revenue</p>
                <p className="text-lg font-bold text-foreground-950 leading-tight">$8.2<span className="text-xs text-foreground-500">k</span></p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Contenido centrado debajo del video */}
        <motion.div style={{ opacity }} className="w-full max-w-[800px] text-center flex flex-col items-center">
          <ScrollReveal direction="up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200/50 text-primary-700 text-xs font-semibold tracking-wide mb-6">
              <i className="ri-fire-line text-sm"></i>
              {t('phoenix.badge')}
            </span>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.1}>
            <p className="font-heading text-sm md:text-base font-semibold text-accent-600 tracking-[0.2em] uppercase mb-3">
              {t('phoenix.heroTitle')}
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.15}>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-[3.35rem] font-bold text-foreground-950 leading-[1.12] tracking-tight mb-5">
              {t('phoenix.heroHeadline')}
            </h1>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.2}>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8 max-w-[620px]">
              {t('phoenix.heroSubtitle')}
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.25}>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-xs font-medium">
                  <i className="ri-check-line text-primary-500 text-sm"></i>
                  {f}
                </span>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.3}>
            <div className="flex items-end justify-center gap-6 mb-8">
              <div>
                <p className="text-xs text-foreground-400 font-medium uppercase tracking-wide mb-1">{t('phoenix.badge').replace('🔥 ', '')}</p>
                <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-950">{t('phoenix.price')}</span>
                <p className="text-xs text-foreground-400 font-medium mt-1">{t('phoenix.pricePerSession')}</p>
              </div>
              <div className="flex items-center gap-1 text-accent-600 pb-1.5">
                <i className="ri-arrow-down-line text-sm"></i>
                <span className="text-xs font-medium">{t('phoenix.pricingSave')}</span>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.35}>
            <div className="flex flex-col sm:flex-row justify-center gap-3 mb-10">
              <a
                href="https://wa.link/em25v1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-[0_4px_20px_rgba(229,57,53,0.3)] hover:shadow-[0_6px_28px_rgba(229,57,53,0.4)] hover:-translate-y-0.5 cursor-pointer"
              >
                {t('phoenix.ctaPrimary')}
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#method"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 hover:border-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('phoenix.ctaSecondary')}
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.4}>
            <div className="flex flex-wrap justify-center gap-6">
              {trustItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-foreground-500 text-xs">
                  <i className={`${item.icon} text-primary-500/60`}></i>
                  {item.text}
                </div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.45}>
            <a
              href="https://wa.link/em25v1"
              target="_blank"
              rel="noopener noreferrer"
              className="animate-bounce-cta mt-10 inline-flex items-center justify-center gap-3 w-full px-6 py-5 rounded-2xl bg-gradient-to-r from-red-600 to-red-500 text-white font-bold text-base md:text-lg hover:from-red-700 hover:to-red-600 transition-colors duration-300 whitespace-nowrap shadow-[0_8px_32px_rgba(220,38,38,0.45)] hover:shadow-[0_12px_40px_rgba(220,38,38,0.55)] cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line text-xl"></i>
              {t('phoenix.ctaBuySessions')}
            </a>
          </ScrollReveal>
        </motion.div>
      </div>
    </section>
  );
}